// frontend/app/components/lessonPlans/teachsense/TeachSenseView.js
//
// The eight TeachSense sections in the order a teacher reads them: what to do
// next, then the evidence for it — where the class is, what they have mastered,
// what they thought going in, what they wrote in their own words, what they are
// getting wrong, what they say they still do not understand, and how they
// worked.
//
// Written answers sit between prior knowledge and misconceptions because that
// is the run of the lesson: what they guessed, what they then wrote, and the
// wrong ideas read out of that writing.
//
// Every section renders its own empty state, so a payload missing a key is one
// quiet card rather than a crash. Nothing here fetches; the panel owns that.

import React from "react";

import { ActionButton } from "../blocks/LessonBlockShell";
import { StateCard } from "./TeachSenseSection";
import TeachSenseExploration from "./TeachSenseExploration";
import TeachSenseLive from "./TeachSenseLive";
import TeachSenseMastery from "./TeachSenseMastery";
import TeachSenseMisconceptions from "./TeachSenseMisconceptions";
import TeachSensePriorKnowledge from "./TeachSensePriorKnowledge";
import TeachSenseRecommendations from "./TeachSenseRecommendations";
import TeachSenseReflections from "./TeachSenseReflections";
import TeachSenseWrittenAnswers from "./TeachSenseWrittenAnswers";

const TeachSenseView = ({
  teachsense = null,
  loading = false,
  error = null,
  onRetry = null,
  // Set when the teacher arrived by pressing "N written answers to read" on a
  // concept in class insights. See the hoist below for what it does.
  focusConcept = null,
  focusLabel = "",
  onClearFocus = null,
  colors,
  isDarkMode = false,
  isNarrow = false,
  t,
}) => {
  const translate = t || ((value) => value);

  // Loading, failed and not-yet-generated share one card, so the view never
  // vanishes out from under the teacher mid-lesson.
  if (loading) {
    return (
      <StateCard
        testID="teachsense-state"
        colors={colors}
        text={translate("Reading the room…")}
      />
    );
  }

  if (error) {
    return (
      <StateCard
        testID="teachsense-state"
        colors={colors}
        icon="alert-outline"
        tone={colors.danger}
        text={translate("TeachSense could not be loaded.")}
        action={
          onRetry ? (
            <ActionButton
              colors={colors}
              label={translate("Retry")}
              icon="refresh"
              variant="outline"
              accent={colors.info}
              onPress={onRetry}
            />
          ) : null
        }
      />
    );
  }

  if (!teachsense) {
    return (
      <StateCard
        testID="teachsense-state"
        colors={colors}
        icon="account-clock-outline"
        text={translate("Nobody has worked through this lesson yet.")}
      />
    );
  }

  const shared = { colors, isDarkMode, isNarrow, t: translate };

  // A teacher who pressed "3 written answers to read" is mid-decision about one
  // concept, and landing them at the top of a page whose fourth card holds what
  // they asked for is the same broken promise one scroll further down. So when
  // a concept is in focus the section is hoisted to the top of the view; with
  // no focus it keeps its place in the reading order. Moving it rather than
  // scrolling to it needs no refs and behaves the same on web and native.
  const writtenAnswers = (
    <TeachSenseWrittenAnswers
      writtenAnswers={teachsense.written_answers}
      focusConcept={focusConcept}
      focusLabel={focusLabel}
      onClearFocus={onClearFocus}
      {...shared}
    />
  );
  const focused = Boolean(String(focusConcept || "").trim());

  return (
    <>
      {focused ? writtenAnswers : null}
      <TeachSenseRecommendations recommendations={teachsense.recommendations} {...shared} />
      <TeachSenseLive live={teachsense.live} meta={teachsense.meta} {...shared} />
      <TeachSenseMastery concepts={teachsense.concepts} {...shared} />
      <TeachSensePriorKnowledge priorKnowledge={teachsense.prior_knowledge} {...shared} />
      {focused ? null : writtenAnswers}
      <TeachSenseMisconceptions misconceptions={teachsense.misconceptions} {...shared} />
      <TeachSenseReflections reflections={teachsense.reflections} {...shared} />
      <TeachSenseExploration exploration={teachsense.exploration} {...shared} />
    </>
  );
};

export default TeachSenseView;
